import * as React from "react";
import styled from "styled-components";

import { border, colors, distances, palette } from "../../styles/constants";
import { IconFrame } from "../Icons";
import { Ban, CheckboxCheck, CheckboxIndeterminate, RadioDot } from "./icons";

export type OnChangeFunction = (name: string, value: boolean) => void;

interface RadiobuttonProps {
    name: string;
    label: any;
    element?: React.ReactNode;
    value: boolean;
    className?: string;
    onChange: OnChangeFunction;
    disabled?: boolean;
    banned?: boolean;
    children?: React.ReactNode;
    [key: string]: any;
}

const Radiobutton = (props: RadiobuttonProps) => {
    const [focus, setFocus] = React.useState(false);
    const dataProps = Object.keys(props).reduce<Record<string, any>>(
        (acc, key) => {
            if (key.startsWith("data-")) {
                acc[key] = props[key];
            }
            return acc;
        },
        {},
    );
    const banned = props.banned || false;
    const half = (props.className || "").split(" ").includes("half");
    return (
        <Label
            className={props.className || ""}
            checked={props.value}
            disabled={props.disabled || false}
        >
            <Box $checked={props.value} $banned={banned} $focus={focus}>
                <BoxGlyph checked={props.value} banned={banned} half={half} />
            </Box>
            <HiddenBox
                type="checkbox"
                checked={props.value}
                name={props.name}
                disabled={props.disabled}
                onChange={(event) => {
                    props.onChange(props.name, event.target.checked);
                }}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                {...dataProps}
            />
            {props.element || props.label}
        </Label>
    );
};

const BoxGlyph = ({
    checked,
    banned,
    half,
}: {
    checked: boolean;
    banned: boolean;
    half: boolean;
}) => {
    if (half) {
        return checked ? (
            <IconFrame
                glyph={CheckboxCheck}
                size="small"
                color={palette.white}
            />
        ) : (
            <IconFrame
                glyph={CheckboxIndeterminate}
                size="small"
                color={palette.neutral[400]}
            />
        );
    }
    if (banned) {
        return (
            <IconFrame glyph={Ban} size="small" color={palette.primary[500]} />
        );
    }
    if (checked) {
        return (
            <IconFrame glyph={RadioDot} size="small" color={palette.white} />
        );
    }
    return null;
};

interface LabelProps {
    checked: boolean;
    disabled: boolean;
}

const Label = styled.label<LabelProps>`
    display: inline-block;
    position: relative;
    user-select: none;
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    opacity: ${(props) => (props.disabled ? "0.4" : "1")};

    &:hover .radiobutton-box {
        border: ${border.normal} solid ${colors.primaryHover};
        border-color: ${(props) => (props.checked ? colors.primary : colors.primaryHover)};
    }

`;

interface BoxProps {
    $checked: boolean;
    $banned: boolean;
    $focus: boolean;
}

const Box = styled.span.attrs({ className: "radiobutton-box" })<BoxProps>`
    position: relative;
    vertical-align: middle;
    width: ${distances.small};
    height: ${distances.small};
    bottom: 1px;
    margin-right: ${distances.tiny};
    background-color: ${(props) => (props.$checked && !props.$banned ? colors.primary : "transparent")};
    border: ${(props) => (props.$checked ? "none" : `${border.normal} solid ${colors.borderDark}`)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    transition: all 100ms ease-out;
    border-radius: 50%;
    ${(props) => (props.$focus ? `border-color:${colors.primaryFocusOutline};` : "")}
`;

const HiddenBox = styled.input`
    opacity: 0;
    height: 0;
    width: 0;
`;

export default Radiobutton;
